// Lib
import React, { useState, useRef, useCallback, useMemo } from 'react';
import type { MutableRefObject } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import MilanoteCellEditorComponent from './modules/MilanoteCellEditorComponent';

// Components
import useTableCellEditingHandlers from './handlers/useTableCellEditingHandlers';
import TableOverlay from './handlers/TableOverlay';
import TablePopups from './TablePopups';
import HotTableRendererPortalManager from './manager/HotTableRendererPortalManager';
import useInitializeHotTable from './useInitializeHotTable';
import useTableSelectionHandlers from './handlers/useTableSelectionHandlers';
import useTableResizeEffects from './handlers/useTableResizeEffects';
import useTableMoveColumnRowHandlers from './handlers/useTableMoveColumnRowHandlers';
import useTableContextMenuHandlers from './handlers/contextMenu/useTableContextMenu';
import TableContextMenuManagerV2 from './handlers/contextMenu/TableContextMenuManagerV2';
import useTableSelectionEffects from './handlers/useTableSelectionEffects';
import useTableGridSizeEffects from './handlers/useTableGridSizeEffects';
import useTableCellEditingEffects from './handlers/useTableCellEditingEffects';
import useManualColumnResize from './handlers/useManualColumnResize';
import useTableUndoRedo from './handlers/useTableUndoRedo';
import { useHotStoreActiveTableSync } from './sync/useHotStoreActiveTableSync';
import { useHotToReduxSession } from './sync/useHotToReduxSession';
import { useReduxToHotRehydration } from './sync/useReduxToHotRehydration';
import { useTableEditingHandlers } from './handlers/useTableEditingHandlers';
import { useActiveTableSelector } from '../bridge/store/hotTableStoreHooks';
import { selectActiveTableCellSelectionForElement } from '../bridge/store/selectors/activeHotTableCellSelectionSelectors';
import { registerHotPlugins } from './hotPluginsRegistration';

// Utils
import { markEventAsHandled } from '../../../utils/react/reactEventUtil';
import { isEditableElement } from '../../../utils/dom/domInputUtils';
import { getWidth } from '../../../../common/elements/utils/elementPropertyUtils';

// Selectors
import {
    getTableContentColWidthsGUSelector,
    isCurrentlyEditingTableCell as selectIsCurrentlyEditingTableCell,
} from './tableSelector';
import { getIsFeatureEnabledForCurrentUser } from '../../feature/elementFeatureSelector';
import { ExperimentId } from '../../../../common/experiments/experimentsConstants';
import { getMilanoteApplicationModeSelector } from '../../../platform/platformSelector';
import { selectDebugToolProperty } from '../../../debug/store/debugToolSelector';
import { DebugTools } from '../../../debug/store/debugToolTypes';
import { getThemeIsDarkMode } from '../../../theme/themeSelector';
import { getCurrentlyEditingEditorIdForElement } from '../../selection/currentlyEditingSelector';
import { userCurrencyPreferenceSelector, userLanguagePreferenceSelector } from '../../../user/currentUserSelector';

// Types
import type { ImMNElement } from '../../../../common/elements/elementModelTypes';
import type { RectSize } from '../../../../common/maths/geometry/rect/rectTypes';
import type { UndoRedoRichCellDataState } from '../bridge/utils/hotInstanceUtils/undoRedo/hotTableUndoRedoRichCellDataUtils';
import { CellEditingSession, TableDataVersion } from '../../../../common/table/TableTypes';
import type { MilanoteEditorRef } from '../../../components/editor/milanoteEditor/MilanoteEditorTypes';
import { MNHotInstance } from '../bridge/tableHotInstanceTypes';
import { useHotTableVersionedData } from './dataConversion/useHotTableVersionedData';

// Styles
import './HotTable.scss';
import './HotTableRowHeight.scss';
import './HotTableRefHeaders.scss';
import './HotContextMenu.scss';
import './HotTableDebug.scss';
import type { FormulaPickerRef } from './useFormulaPickerController';

export type HotTableProps = {
    element: ImMNElement;
    elementId: string;
    hotTableInstanceRef: MutableRefObject<MNHotInstance | null>;
    gridSize: number;
    tempSize?: RectSize | null;
    currentlyEditingEditorId?: string | null;
    currentEditorKey?: string | null;
    isDragging: boolean;
    isEditable: boolean;
    isSingleSelected: boolean;
    isEditing: boolean;
    isResizing: boolean;
    inList?: string;
    isReadOnly: boolean;
    getContextZoomScale: () => number;
    showTitle: boolean;
    showCaption: boolean;
    showBottomPanel: boolean;
    filterQuery?: string;
};

type HotTableHookProps = HotTableProps & {
    locale: string;
    currencyPreference: string;
    currentlyEditingEditorId: string | null;
    hotTableContainerRef: MutableRefObject<HTMLDivElement | null>;
    isCurrentlyEditingTableCell: boolean;
    isDarkMode: boolean;
    tableContentColWidthsGU: number[];
    undoRedoRichCellDataState: UndoRedoRichCellDataState;
};

registerHotPlugins();

const contextMenuV2EnabledSelector = getIsFeatureEnabledForCurrentUser(ExperimentId.contextMenuV2);

const HotTable: React.FC<HotTableProps> = (ownProps) => {
    const {
        element,
        elementId,
        hotTableInstanceRef,
        gridSize,
        inList,
        isDragging,
        isReadOnly,
        isResizing,
        getContextZoomScale,
        showTitle,
        showCaption,
        showBottomPanel,
        filterQuery,
        isSingleSelected,
        isEditing,
    } = ownProps;

    const { data: tableContentData, version } = useHotTableVersionedData(element);

    const locale = useSelector(userLanguagePreferenceSelector);

    // Handsontable settings are fixed at mount, so freeze the experiment state to keep the Roma menu
    // and the HOT contextMenu config consistent for this instance's lifetime.
    const contextMenuV2Enabled = useSelector(contextMenuV2EnabledSelector);
    const [isContextMenuV2Enabled] = useState(contextMenuV2Enabled);

    // We need a memoised version of the selector factory result, so that's why we're doing this
    const tableContentColWidthsGUSelector = useMemo(() => getTableContentColWidthsGUSelector(), []);
    const currentlyEditingEditorIdSelector = useMemo(
        () => getCurrentlyEditingEditorIdForElement(elementId),
        [elementId],
    );
    const isCurrentlyEditingTableCellSelector = useMemo(
        () => selectIsCurrentlyEditingTableCell(elementId),
        [elementId],
    );

    // @ts-ignore JS selector can't interpret the second argument
    const tableContentColWidthsGU = useSelector((state) => tableContentColWidthsGUSelector(state, { element }) || []);
    const currentlyEditingEditorId = useSelector(currentlyEditingEditorIdSelector);
    const isCurrentlyEditingTableCell = useSelector(isCurrentlyEditingTableCellSelector);
    const isDarkMode = useSelector(getThemeIsDarkMode);
    const currencyPreference = useSelector(userCurrencyPreferenceSelector);
    const showCellRendererBadge = useSelector(selectDebugToolProperty(DebugTools.TABLE, 'showCellRendererBadge'));
    const appMode = useSelector(getMilanoteApplicationModeSelector);

    const [cellEditingSession, setCellEditingSession] = useState<CellEditingSession | null>(null);

    const cellEditorRef = useRef<MilanoteEditorRef | null>(null);
    const formulaPickerRef = useRef<FormulaPickerRef | null>(null);

    // This stores rich Milanote cell data so that text styling etc is maintained when undoing and redoing
    const undoRedoRichCellDataStateRef = useRef<UndoRedoRichCellDataState>({});

    const hotTableContainerRef = useRef<HTMLDivElement | null>(null);
    const hotTableRendererPortalManagerRef = useRef(null);

    const elementWidth = getWidth(element);
    const isActive = isEditing || isSingleSelected;

    // Sync hot instance -> main redux store
    const activeTableSession = useHotToReduxSession({
        tableInstance: hotTableInstanceRef.current,
        elementId,
        isActive,
        isResizing,
        gridSize,
        elementWidth,
        tableContentData,
        tableContentColWidthsGU,
    });

    // Sync main redux store -> hot instance
    useReduxToHotRehydration({
        elementId,
        tableInstance: hotTableInstanceRef.current,
        isActive,
        gridSize,
        inList,
        hotTableContainerRef,
        getContextZoomScale,
        tableContentData,
        tableContentColWidthsGU,
        activeTableSession,
    });

    // Sync hot instance -> *hot* redux store
    useHotStoreActiveTableSync({
        tableInstance: hotTableInstanceRef.current,
        elementId,
        isActive,
        session: activeTableSession,
        tableDataVersion: version,
    });

    // NOTE: "props" is defined below to avoid having to change all the usages within this file after the
    //  hotTableContainerRef was moved into this component.
    const props: HotTableHookProps = {
        ...ownProps,
        locale,
        currencyPreference,
        currentlyEditingEditorId,
        hotTableContainerRef,
        isCurrentlyEditingTableCell,
        isDarkMode,
        tableContentColWidthsGU,
        undoRedoRichCellDataState: undoRedoRichCellDataStateRef.current,
    };

    const {
        jumpToCaption,
        jumpToTitle,
        startEditingCellEditor,
        finishEditingCellEditor,
        dispatchStartEditingTableGrid,
        deselectElement,
    } = useTableEditingHandlers({
        hotTableInstanceRef,
        showTitle,
        showCaption,
        elementId,
        isCurrentlyEditingTableCell,
        setCellEditingSession,
    });
    const tableCellEditingHandlers = useTableCellEditingHandlers(props);
    const tableCellSelectionHandlers = useTableSelectionHandlers({ ...props, dispatchStartEditingTableGrid });
    const tableMoveColumnRowHandlers = useTableMoveColumnRowHandlers(props);
    const tableContextMenuHandlers = useTableContextMenuHandlers(props);

    const mounted = useInitializeHotTable({
        elementId,
        tableDataVersion: version,
        tableContentData,
        tableContentColWidthsGU,
        hotTableInstanceRef,
        hotTableContainerRef,
        hotTableRendererPortalManagerRef,
        gridSize,
        isDarkMode,
        inList,
        isContextMenuV2Enabled,
        isReadOnly,
        isResizing,
        isSingleSelected,
        getContextZoomScale,
        showTitle,
        showBottomPanel,
        locale,
        setCellEditingSession,
        startEditingCellEditor,
        finishEditingCellEditor,
        deselectElement,
        tableCellEditingHandlers,
        tableCellSelectionHandlers,
        tableMoveColumnRowHandlers,
        tableContextMenuHandlers,
        jumpToCaption,
        jumpToTitle,
        cellEditorRef,
        formulaPickerRef,
        undoRedoRichCellDataState: undoRedoRichCellDataStateRef.current,
        appMode,
    });

    // These need to be called after the effect that initializes the hotTableInstanceRef
    useTableUndoRedo(hotTableInstanceRef.current, undoRedoRichCellDataStateRef.current);
    useTableCellEditingEffects(props);
    useTableSelectionEffects(props);
    useTableGridSizeEffects(props);
    useTableResizeEffects(props);
    useManualColumnResize({
        getContextZoomScale,
        hotTableContainerRef,
        hotTableInstanceRef,
    });

    const onMouseDown = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            const target = event.target as HTMLElement;
            const isTableUtility = target.closest('[data-table-util]');

            // Prevent a click to data-table-utils DOM element from blurring the active cell editor,
            // unless they are an input that needs focus.
            if (isCurrentlyEditingTableCell && isTableUtility && !isEditableElement(target)) {
                event.preventDefault();
            }

            // This is done in order to stop the parent element (in ElementContainer) to handle this event when other areas of
            // the table is clicked.
            if (isSingleSelected) markEventAsHandled(event);
        },
        [isCurrentlyEditingTableCell, isSingleSelected],
    );

    const nRows = hotTableInstanceRef.current?.countRows() || 0;
    const nCols = hotTableInstanceRef.current?.countCols() || 0;

    const currentCellSelections = useActiveTableSelector(selectActiveTableCellSelectionForElement(elementId));

    const isSelectedLastRow = currentCellSelections?.some(
        ([startRow, , endRow]) => startRow === nRows - 1 || endRow === nRows - 1,
    );

    const isSelectedLastCol = currentCellSelections?.some(
        ([, startCol, , endCol]) => startCol === nCols - 1 || endCol === nCols - 1,
    );

    const hotTable = (
        <div
            className={classNames('HotTable', {
                'is-selected-last-col': isSelectedLastCol,
                'is-selected-last-row': isSelectedLastRow,
                'debug-cell-renderer-badges': showCellRendererBadge,
                // TODO-TABLES remove these debug visualisers
                'is-table-data-v2': version === TableDataVersion.V2,
                'is-table-data-v3': version === TableDataVersion.V3,
            })}
            onMouseDown={onMouseDown}
        >
            <TableOverlay {...props} />

            <TablePopups
                {...props}
                cellEditingSessionId={cellEditingSession?.sessionId}
                formulaPickerRef={formulaPickerRef}
                isActiveTable={isActive}
            />

            <MilanoteCellEditorComponent
                {...props}
                cellEditorRef={cellEditorRef}
                hotTableContainerRef={hotTableContainerRef}
                hotTableInstanceRef={hotTableInstanceRef}
                gridSize={gridSize}
                cellEditingSession={cellEditingSession}
                version={version}
                startEditingCellEditor={startEditingCellEditor}
                // Pass in mounted prop to ensure that the MilanoteCellEditorComponent
                // is re-rendered after HotTable is mounted
                mounted={mounted}
            />

            <HotTableRendererPortalManager
                ref={hotTableRendererPortalManagerRef}
                elementId={elementId}
                version={version}
                locale={locale}
                filterQuery={filterQuery}
            />

            <div className="HotTableLib" ref={hotTableContainerRef}></div>
        </div>
    );

    if (!isContextMenuV2Enabled) return hotTable;

    const dismissTableContextMenu =
        !isSingleSelected || isCurrentlyEditingTableCell || isDragging || isResizing || !currentCellSelections?.length;

    return (
        <TableContextMenuManagerV2
            hotTableInstanceRef={hotTableInstanceRef}
            locale={locale}
            elementId={elementId}
            dismissWhen={dismissTableContextMenu}
        >
            {hotTable}
        </TableContextMenuManagerV2>
    );
};

export default HotTable;
